type ProjectId = 'modernGarden' | 'lawnRenovation' | 'seasonalPlanting' | 'patioInstallation' | 'waterFeature' | 'gardenMaintenance'

interface Project {
  title: string
  description: string
  category: string
  specifications?: {
    projectType: string
    location: string
    duration: string
    gardenSize: string
    challenges: string
  }
  process?: string[]
  results?: string[]
  clientFeedback?: string
  beforeAfter?: {
    before: string
    after: string
  }
  afterImage?: string
}

export interface Categories {
  all: string
  design: string
  restoration: string
  lawnCare: string
  planting: string
}

interface PortfolioTranslations {
  title: string
  subtitle: string
  categories: Categories
  projects: Record<ProjectId, Project>
  viewDetails: string
  modal: {
    specifications: string
    projectType: string
    location: string
    gardenSize: string
    duration: string
    keyChallenges: string
    process: string
    results: string
    beforeAfter: string
    before: string
    after: string
    clientFeedback: string
  }
}

export const portfolioTranslations: Record<'en' | 'cy', PortfolioTranslations> = {
  en: {
    title: 'My Portfolio',
    subtitle: 'See the transformations I\'ve achieved through garden maintenance, restoration, and improvement work',
    categories: {
      all: 'All',
      design: 'Design',
      restoration: 'Restoration',
      lawnCare: 'Lawn Care',
      planting: 'Planting'
    },
    projects: {
      modernGarden: {
        title: 'Garden Clearance & Restoration',
        description: 'Complete transformation of a garden that needed attention into a clean, usable outdoor space. This project involved careful clearance of overgrown vegetation and restoration to a tidy, functional condition.',
        category: 'restoration',
        specifications: {
          projectType: 'Garden Clearance & Restoration',
          location: 'South Wales',
          duration: '3 days',
          gardenSize: 'Medium-sized residential garden',
          challenges: 'Heavy overgrowth, thorny vegetation, limited access'
        },
        process: [
          'Initial assessment and safety evaluation',
          'Systematic clearance of overgrown vegetation',
          'Careful removal of thorny plants and weeds',
          'Disposal of garden waste and debris',
          'Final tidying and restoration of usable space'
        ],
        results: [
          'Completely cleared and restored garden space',
          'Safe and accessible outdoor area',
          'Ready for new planting and landscaping',
          'Significant improvement in property value',
          'Client able to enjoy their garden again'
        ],
        clientFeedback: 'We were amazed at how James transformed our completely overgrown garden into a clean, usable space in just a few days. His attention to detail and careful approach meant no damage to the property. We can now actually use our garden again!',
        beforeAfter: {
          before: 'Garden with overgrown vegetation that made the space challenging to use',
          after: 'Clean, tidy garden space ready for new landscaping and enjoyment'
        },
        afterImage: '/images/portfolio/after-1.webp'
      },
      lawnRenovation: {
        title: 'Fence Replacement & Low-Maintenance Garden Upgrade',
        description: 'Complete fence replacement with new sturdy panels and conversion to artificial grass for year-round enjoyment with minimal maintenance. This upgrade transformed a high-maintenance garden into a practical, low-upkeep outdoor space.',
        category: 'design',
        specifications: {
          projectType: 'Fence Replacement & Garden Upgrade',
          location: 'South Wales',
          duration: '4 days',
          gardenSize: 'Medium residential garden',
          challenges: 'Old worn fencing, difficult natural turf maintenance, limited access for materials'
        },
        process: [
          'Assessment of existing fence condition and removal planning',
          'Careful removal of old fencing and disposal of materials',
          'Installation of new sturdy fence panels with proper foundations',
          'Removal of existing natural turf and soil preparation',
          'Installation of high-quality artificial grass with proper drainage',
          'Final finishing touches and quality inspection'
        ],
        results: [
          'Secure, attractive new fencing that enhances property value',
          'Low-maintenance artificial grass for year-round enjoyment',
          'Significantly reduced garden maintenance requirements',
          'Clean, practical outdoor space for family use',
          'Improved property security and curb appeal'
        ],
        clientFeedback: 'Absolutely incredible work! James replaced our old fence and installed artificial grass, and the transformation is unbelievable. The new fence is solid and looks great, and the artificial grass means we can enjoy our garden all year without the constant maintenance.',
        beforeAfter: {
          before: 'Old, worn-out fencing and difficult-to-maintain natural turf requiring constant upkeep',
          after: 'New sturdy fencing with low-maintenance artificial grass for year-round enjoyment'
        },
        afterImage: '/images/portfolio/after-2.webp'
      },
      seasonalPlanting: {
        title: 'Flower Bed Pruning & Garden Tidy-Up',
        description: 'This garden had a flower bed where the plants had grown beyond their intended borders and needed shaping. The job involved careful pruning and maintenance to bring the flowers back to their best form. With thoughtful shaping and care, the bed was restored to a more attractive and well-maintained state, helping to enhance the overall look of the garden.',
        category: 'planting',
        specifications: {
          projectType: 'Flower Bed Pruning & Garden Tidy-Up',
          location: 'South Wales',
          duration: '2 days',
          gardenSize: 'Small to medium residential garden',
          challenges: 'Overgrown plants, lost shape and structure, plants spreading beyond borders'
        },
        process: [
          'Assessment of flower bed condition and plant health',
          'Careful pruning of overgrown plants to restore shape',
          'Removal of dead or damaged foliage and stems',
          'Shaping and training plants back within their borders',
          'General garden tidy-up and debris removal',
          'Final inspection and light maintenance advice'
        ],
        results: [
          'Restored flower bed with proper plant shape and structure',
          'Plants contained within their designated borders',
          'Improved overall garden appearance and curb appeal',
          'Healthier plants with better air circulation',
          'Clean, tidy garden space ready for ongoing maintenance'
        ],
        clientFeedback: 'Our flower bed was completely out of control, but James worked his magic! The plants look so much better now - they have their shape back and aren\'t taking over the garden anymore. The whole area looks much more attractive and well-maintained.',
        beforeAfter: {
          before: 'Flower bed with plants that had grown beyond their intended borders and lost their natural shape',
          after: 'Neatly pruned and shaped flower bed with plants contained and restored to attractive appearance'
        },
        afterImage: '/images/portfolio/after-3.webp'
      },
      patioInstallation: {
        title: 'Pressure Washing & Restoration',
        description: 'This job involved pressure washing the patio area around the house to remove a build-up of mud, dirt, and grime that had accumulated over time. The thorough clean helped to restore the original colour and appearance of the paving, bringing the patio tiles back to their former glory and leaving the outdoor space looking fresh and well-maintained.',
        category: 'restoration',
        specifications: {
          projectType: 'Pressure Washing & Restoration',
          location: 'South Wales',
          duration: '1 day',
          gardenSize: 'Medium residential patio area',
          challenges: 'Heavy build-up of dirt and grime, stained paving, moss and algae growth'
        },
        process: [
          'Assessment of patio condition and staining levels',
          'Preparation of area and protection of surrounding surfaces',
          'Application of appropriate cleaning solutions',
          'Pressure washing with correct pressure settings',
          'Targeted treatment of stubborn stains and moss',
          'Final rinse and inspection of results'
        ],
        results: [
          'Completely restored patio paving to original appearance',
          'Removed years of accumulated dirt, grime, and stains',
          'Eliminated moss and algae growth',
          'Enhanced outdoor space with fresh, clean appearance',
          'Improved property curb appeal and value'
        ],
        clientFeedback: 'The pressure washing work was absolutely incredible! Our patio looks like new again. The difference is amazing - all the years of dirt and grime are gone, and the paving has its original colour back. The whole outdoor area looks so much more inviting now.',
        beforeAfter: {
          before: 'Patio area covered in years of accumulated mud, dirt, grime, and moss growth',
          after: 'Freshly cleaned patio with restored paving showing original colours and clean appearance'
        },
        afterImage: '/images/portfolio/after-4.webp'
      },
      waterFeature: {
        title: 'Patio Replacement with Decking',
        description: 'The original stone patio, once a sunny spot for relaxing, had begun to crack, break apart, and shift over time, making it uneven and unsafe to use. Rather than replacing it with the same stone slabs, the decision was made to install decking instead. This not only created a safer, more stable surface but also transformed the area into a more inviting space—ideal for soaking up the sun and entertaining guests.',
        category: 'design',
        specifications: {
          projectType: 'Patio Replacement with Decking',
          location: 'South Wales',
          duration: '3 days',
          gardenSize: 'Medium residential garden',
          challenges: 'Damaged and unsafe stone patio, uneven surface, need for stable replacement'
        },
        process: [
          'Assessment of existing patio damage and safety concerns',
          'Removal of broken and damaged stone slabs',
          'Preparation of ground surface and foundation work',
          'Installation of decking framework and supports',
          'Fitting of decking boards with proper spacing and finishing',
          'Final inspection and safety testing'
        ],
        results: [
          'Safe, stable decking surface replacing dangerous stone patio',
          'Enhanced outdoor living space for relaxation and entertainment',
          'Improved property value with modern decking installation',
          'Better drainage and maintenance compared to stone patio',
          'More inviting and comfortable outdoor area'
        ],
        clientFeedback: 'What a fantastic transformation! James replaced our broken stone patio with beautiful decking, and it\'s now a safe, stable area where we can relax and entertain. The decking looks modern and feels so much more comfortable underfoot than the old cracked stones.',
        beforeAfter: {
          before: 'Damaged stone patio with cracked, broken, and uneven slabs creating safety hazards',
          after: 'New decking installation providing safe, stable, and inviting outdoor living space'
        },
        afterImage: '/images/portfolio/after-5.webp'
      },
      gardenMaintenance: {
        title: 'Lawn Recovery & First Cut',
        description: 'The lawn had grown longer than usual and needed attention to restore it to a healthy, tidy state. This job involved cutting it back and beginning the process of restoring the lawn to its best condition. With regular care and attention moving forward, the garden is now well on its way back to its former green and lush condition.',
        category: 'lawnCare',
        specifications: {
          projectType: 'Lawn Recovery & First Cut',
          location: 'South Wales',
          duration: '2 days',
          gardenSize: 'Medium residential garden',
          challenges: 'Long grass, lawn requiring recovery and maintenance'
        },
        process: [
          'Assessment of lawn condition and grass height',
          'Initial cutting to restore manageable grass length',
          'Removal of grass clippings and debris',
          'Inspection of lawn health and soil condition',
          'Application of lawn care treatments if needed',
          'Establishment of maintenance schedule for ongoing care'
        ],
        results: [
          'Successfully restored lawn to manageable and tidy appearance',
          'Improved lawn health and growth potential',
          'Established foundation for ongoing lawn maintenance',
          'Enhanced overall garden appearance and curb appeal',
          'Lawn ready for regular maintenance schedule'
        ],
        clientFeedback: 'Our lawn was a complete mess, but James sorted it out perfectly! It looks so much better now - like a proper garden again. He\'s set us up with a good maintenance plan so we can keep it looking great going forward. The transformation is really impressive.',
        beforeAfter: {
          before: 'Lawn with long grass that had grown beyond normal maintenance height',
          after: 'Neatly cut and recovered lawn showing healthy grass and tidy appearance ready for ongoing maintenance'
        },
        afterImage: '/images/portfolio/after-6.webp'
      }
    },
    viewDetails: 'View Details',
    modal: {
      specifications: 'Specifications',
      projectType: 'Project Type',
      location: 'Location',
      gardenSize: 'Garden Size',
      duration: 'Duration',
      keyChallenges: 'Key Challenges',
      process: 'Process',
      results: 'Results',
      beforeAfter: 'Before and After',
      before: 'Before',
      after: 'After',
      clientFeedback: 'Client Feedback'
    }
  },
  cy: {
    title: 'Fy Mhortffolio',
    subtitle: 'Gweld y trawsnewidiadau rwyf wedi\'u cyflawni drwy waith cynnal a chadw, adfer, a gwella gardd',
    categories: {
      all: 'Y Cyfan',
      design: 'Dylunio',
      restoration: 'Adfer',
      lawnCare: 'Gofal Lawnt',
      planting: 'Plannu'
    },
    projects: {
      modernGarden: {
        title: 'Clirio a Adfer Gardd',
        description: 'Trawsnewid cyflawn o ardd wedi\'i gor-dyfu yn lle awyr agored glân a defnyddiadwy. Roedd y prosiect hwn yn cynnwys clirio llawn o dyfiant gwyllt ac adfer i gyflwr taclus a gweithredol.',
        category: 'restoration',
        specifications: {
          projectType: 'Clirio a Adfer Gardd',
          location: 'De Cymru',
          duration: '3 diwrnod',
          gardenSize: 'Gardin preswyl o faint canolig',
          challenges: 'Gor-dyfiant trwm, tyfiant pigog, mynediad cyfyngedig'
        },
        process: [
          'Asesiad cychwynnol a gwerthuso diogelwch',
          'Clirio systematig o dyfiant wedi\'i gor-dyfu',
          'Dileu gofalus o blanhigion pigog a chwyn',
          'Gwaredu gwastraff gardd a malurion',
          'Tacluso terfynol ac adfer lle defnyddiadwy'
        ],
        results: [
          'Lle gardd wedi\'i glirio ac adfer yn llwyr',
          'Ardal awyr agored ddiogel a hygyrch',
          'Yn barod ar gyfer plannu a thirlunio newydd',
          'Gwelliant sylweddol yng ngwerth eiddo',
          'Cleient yn gallu mwynhau eu gardd eto'
        ],
        clientFeedback: 'Roeddem yn synnu at sut y trawsnewidiodd James ein gardd wedi\'i gor-dyfu yn llwyr yn lle glân a defnyddiadwy mewn dim ond ychydig o ddyddiau. Roedd ei sylw i fanylion a\'i ddull gofalus yn golygu dim difrod i\'r eiddo. Gallwn nawr ddefnyddio ein gardd eto!',
        beforeAfter: {
          before: 'Wedi\'i gor-dyfu\'n drwm gyda chwyn, pigau, a thyfiant gwyllt gan wneud y lle yn anfanteisiol',
          after: 'Lle gardd glân, taclus yn barod ar gyfer thirlunio newydd a mwynhad'
        },
        afterImage: '/images/portfolio/after-1.webp'
      },
      lawnRenovation: {
        title: 'Adnewyddu Lawnt',
        description: 'Rhaglen adfer a chynnal a chadw lawnt gyflawn ar gyfer eiddo preswyl.',
        category: 'design',
        specifications: {
          projectType: 'Fence Replacement & Garden Upgrade',
          location: 'South Wales',
          duration: '4 days',
          gardenSize: 'Medium residential garden',
          challenges: 'Old worn fencing, difficult natural turf maintenance, limited access for materials'
        },
        process: [
          'Assessment of existing fence condition and removal planning',
          'Careful removal of old fencing and disposal of materials',
          'Installation of new sturdy fence panels with proper foundations',
          'Removal of existing natural turf and soil preparation',
          'Installation of high-quality artificial grass with proper drainage',
          'Final finishing touches and quality inspection'
        ],
        results: [
          'Secure, attractive new fencing that enhances property value',
          'Low-maintenance artificial grass for year-round enjoyment',
          'Significantly reduced garden maintenance requirements',
          'Clean, practical outdoor space for family use',
          'Improved property security and curb appeal'
        ],
        clientFeedback: 'James did an amazing job replacing our old fence and installing artificial grass. The new fence is solid and looks great, and the artificial grass means we can enjoy our garden all year without the constant maintenance. The transformation is incredible!',
        beforeAfter: {
          before: 'Old, worn-out fencing and difficult-to-maintain natural turf requiring constant upkeep',
          after: 'New sturdy fencing with low-maintenance artificial grass for year-round enjoyment'
        },
        afterImage: '/images/portfolio/after-2.webp'
      },
      seasonalPlanting: {
        title: 'Tocio Gwely Blodau a Thacluso Gardd',
        description: 'Roedd gan y ardd hon wely blodau lle\'r oedd y planhigion wedi\'u gadael i or-dyfu, gan dywallt y tu hwnt i\'w ffiniau a cholli eu siâp. Roedd y gwaith yn cynnwys tocio gofalus a ychydig o GGC i ddod â\'r blodau yn ôl o dan reolaeth. Gyda rhywfaint o siapio meddylgar a chynnal a chadw, cafodd y gwely ei adfer i gyflwr mwy deniadol a pleserus i\'r llygad, gan helpu i wella golwg cyffredinol y ardd.',
        category: 'planting',
        specifications: {
          projectType: 'Tocio Gwely Blodau a Thacluso Gardd',
          location: 'De Cymru',
          duration: '2 diwrnod',
          gardenSize: 'Gardin preswyl bach i ganolig',
          challenges: 'Planhigion wedi\'u gor-dyfu, siâp a strwythur wedi\'u colli, planhigion yn lledaenu y tu hwnt i ffiniau'
        },
        process: [
          'Asesiad cyflwr gwely blodau ac iechyd planhigion',
          'Tocio gofalus o blanhigion wedi\'u gor-dyfu i adfer siâp',
          'Dileu dail neu goesynnau marw neu wedi\'u difrodi',
          'Siapio a hyfforddi planhigion yn ôl o fewn eu ffiniau',
          'Tacluso cyffredinol gardd a thynnu malurion',
          'Archwiliad terfynol a chyngor cynnal a chadw ysgafn'
        ],
        results: [
          'Gwely blodau wedi\'i adfer gyda siâp a strwythur planhigion priodol',
          'Planhigion wedi\'u cynnwys o fewn eu ffiniau dynodedig',
          'Gwelliant yn apêl cyffredinol y ardd a stryd',
          'Planhigion iachach gyda cylchrediad aer gwell',
          'Lle gardd glân, taclus yn barod ar gyfer cynnal a chadw parhaus'
        ],
        clientFeedback: 'Roedd ein gwely blodau yn llwyr allan o reolaeth, ond gweithiodd James ei hud! Mae\'r planhigion yn edrych mor well nawr - maent wedi cael eu siâp yn ôl ac nid ydynt yn cymryd drosodd y ardd mwyach. Mae\'r ardal gyfan yn edrych llawer mwy deniadol ac wedi\'i chynnal a\'i chadw\'n dda.',
        beforeAfter: {
          before: 'Gwely blodau wedi\'i gor-dyfu gyda phlanhigion yn tywallt y tu hwnt i ffiniau a cholli eu siâp naturiol',
          after: 'Gwely blodau wedi\'i docio\'n daclus a\'i siapio gyda phlanhigion wedi\'u cynnwys ac wedi\'u adfer i apêr deniadol'
        },
        afterImage: '/images/portfolio/after-3.webp'
      },
      patioInstallation: {
        title: 'Golchi Pwysedd ac Adfer',
        description: 'Roedd y gwaith hwn yn cynnwys golchi pwysedd ardal y patio o amgylch y tŷ i dynnu croniad o fwd, baw, a baw a oedd wedi cronni dros amser. Fe helpodd y glanhau trylwyr i adfer lliw a golwg gwreiddiol y palmant, gan ddod â theils y patio yn ôl i\'w gogoniant blaenorol a gadael y lle awyr agored yn edrych yn ffres ac wedi\'i gynnal a\'i gadw\'n dda.',
        category: 'restoration',
        specifications: {
          projectType: 'Golchi Pwysedd ac Adfer',
          location: 'De Cymru',
          duration: '1 diwrnod',
          gardenSize: 'Ardal patio preswyl ganolig',
          challenges: 'Croniad trwm o faw a baw, palmant wedi\'i staenio, twf mwsogl ac alga'
        },
        process: [
          'Asesiad cyflwr patio a lefelau staenio',
          'Paratoi ardal a diogelu arwynebau o amgylch',
          'Cymhwyso atebion glanhau priodol',
          'Golchi pwysedd gyda lleoliadau pwysedd cywir',
          'Triniaeth darged o staeniau a mwsogl cyndyn',
          'Rinsio terfynol ac archwiliad canlyniadau'
        ],
        results: [
          'Adfer palmant patio yn llwyr i\'w apêr gwreiddiol',
          'Tynnu blynyddoedd o faw, baw, a staeniau cronedig',
          'Dileu twf mwsogl ac alga',
          'Gwella lle awyr agored gyda apêr ffres, glân',
          'Gwelliant apêl stryd eiddo a gwerth'
        ],
        clientFeedback: 'Roedd y gwaith golchi pwysedd yn anhygoel yn llwyr! Mae ein patio yn edrych fel newydd eto. Mae\'r gwahaniaeth yn anhygoel - mae\'r holl faw a baw o\'r blynyddoedd wedi mynd, ac mae\'r palmant wedi cael ei liw gwreiddiol yn ôl. Mae\'r ardal awyr agored gyfan yn edrych mor fwy croesawgar nawr.',
        beforeAfter: {
          before: 'Ardal patio wedi\'i gorchuddio mewn blynyddoedd o fwd, baw, baw, a thwf mwsogl cronedig',
          after: 'Patio wedi\'i lanhau\'n ffres gyda phalmant wedi\'i adfer yn dangos lliwiau gwreiddiol ac apêr glân'
        },
        afterImage: '/images/portfolio/after-4.webp'
      },
      waterFeature: {
        title: 'Disodli Patio gyda Decing',
        description: 'Roedd y patio cerrig gwreiddiol, a fu unwaith yn fan heulog ar gyfer ymlacio, wedi dechrau cracio, torri ar wahân, a symud dros amser, gan ei wneud yn anwastad ac yn anfetyd i\'w ddefnyddio. Yn hytrach na\'i ddisodli gyda\'r un llechi cerrig, penderfynwyd gosod decing yn lle. Nid yn unig y creodd hyn arwyneb diogelach, mwy sefydlog ond hefyd y trawsnewidiodd yr ardal yn lle mwy croesawgar - yn ddelfrydol ar gyfer mwynhau\'r haul a chroesawu gwesteion.',
        category: 'design',
        specifications: {
          projectType: 'Disodli Patio gyda Decing',
          location: 'De Cymru',
          duration: '3 diwrnod',
          gardenSize: 'Gardin preswyl ganolig',
          challenges: 'Patio cerrig wedi\'i ddifrodi ac yn anfetyd, arwyneb anwastad, angen am ddisodliad sefydlog'
        },
        process: [
          'Asesiad difrod patio presennol a phryderon diogelwch',
          'Tynnu llechi cerrig wedi\'u torri a\'u difrodi',
          'Paratoi arwyneb y ddaear a gwaith sylfaen',
          'Gosod fframwaith decing a chymorth',
          'Ffitio byrddau decing gyda gofod priodol a gorffen',
          'Archwiliad terfynol a phrofi diogelwch'
        ],
        results: [
          'Arwyneb decing diogel, sefydlog yn disodli patio cerrig peryglus',
          'Lle awyr agored wedi\'i wella ar gyfer ymlacio a diddanu',
          'Gwerth eiddo wedi\'i wella gyda gosodiad decing modern',
          'Draenio a chynnal a chadw gwell o\'i gymharu â patio cerrig',
          'Ardal awyr agored fwy croesawgar a chyfforddus'
        ],
        clientFeedback: 'Beth drawsnewid gwych! Disodlodd James ein patio cerrig wedi\'i dorri gyda decing hardd, ac mae\'n awr yn ardal ddiogel, sefydlog lle gallwn ymlacio a diddanu. Mae\'r decing yn edrych yn fodern ac yn teimlo llawer mwy cyfforddus o dan draed na\'r hen gerrig wedi\'u cracio.',
        beforeAfter: {
          before: 'Patio cerrig wedi\'i ddifrodi gyda llechi wedi\'u cracio, wedi\'u torri, ac anwastad yn creu peryglon diogelwch',
          after: 'Gosodiad decing newydd yn darparu lle awyr agored diogel, sefydlog, a chroesawgar'
        },
        afterImage: '/images/portfolio/after-5.webp'
      },
      gardenMaintenance: {
        title: 'Adfer Lawnt a\'r Torri Cyntaf',
        description: 'Roedd y lawnt wedi\'i adael heb ei dorri am amser hir, gan ganiatáu i\'r glaswellt dyfu\'n wyllt ac yn afreolus. Roedd y gwaith hwn yn cynnwys ei dorri\'n ôl yn iawn a dechrau\'r broses o adfer y lawnt i gyflwr iach a thaclus. Gyda gofal a sylw rheolaidd yn symud ymlaen, mae\'r ardd nawr yn dda ar ei ffordd yn ôl i\'w chyflwr gwyrdd a chyfoethog blaenorol.',
        category: 'lawnCare',
        specifications: {
          projectType: 'Adfer Lawnt a\'r Torri Cyntaf',
          location: 'De Cymru',
          duration: '2 diwrnod',
          gardenSize: 'Gardin preswyl ganolig',
          challenges: 'Glaswellt wedi\'i gor-dyfu\'n ddifrifol, lawnt wedi\'i esgeuluso sy\'n gofyn am adfer dwys'
        },
        process: [
          'Asesiad cyflwr lawnt a uchder glaswellt',
          'Torri trwm cychwynnol i dynnu glaswellt wedi\'i gor-dyfu',
          'Tynnu torion glaswellt a malurion',
          'Archwiliad iechyd lawnt a chyflwr pridd',
          'Cymhwyso triniaethau gofal lawnt os oes angen',
          'Sefydlu amserlen cynnal a chadw ar gyfer gofal parhaus'
        ],
        results: [
          'Torri\'n llwyddiannus lawnt wedi\'i gor-dyfu\'n ddifrifol',
          'Adfer lawnt i apêr rheolaethwy a thaclus',
          'Gwelliant iechyd lawnt a phosibilrwydd twf',
          'Sefydlu sylfaen ar gyfer cynnal a chadw lawnt parhaus',
          'Gwella apêr cyffredinol y ardd a stryd'
        ],
        clientFeedback: 'Roedd ein lawnt yn llanast llwyr, ond fe\'i setlodd James yn berffaith! Mae\'n edrych mor well nawr - fel gardd briodol eto. Mae wedi ein sefydlu gyda chynllun cynnal a chadw da fel y gallwn ei gadw\'n edrych yn wych yn symud ymlaen. Mae\'r trawsnewid yn drawiadol iawn.',
        beforeAfter: {
          before: 'Lawnt wedi\'i gor-dyfu\'n ddifrifol gyda glaswellt wyllt, afreolus a oedd wedi\'i adael heb ei dorri am gyfnod estynedig',
          after: 'Lawnt wedi\'i dorri\'n daclus ac wedi\'i adfer yn dangos glaswellt iach ac apêr taclus yn barod ar gyfer cynnal a chadw parhaus'
        },
        afterImage: '/images/portfolio/after-6.webp'
      }
    },
    viewDetails: 'Gweld Manylion',
    modal: {
      specifications: 'Manylebau',
      projectType: 'Math o Brosiect',
      location: 'Lleoliad',
      gardenSize: 'Maint y Gardd',
      duration: 'Hyd',
      keyChallenges: 'Heriau Allweddol',
      process: 'Proses',
      results: 'Canlyniadau',
      beforeAfter: 'Cyn ac Ar Ôl',
      before: 'Cyn',
      after: 'Ar Ôl',
      clientFeedback: 'Adborth Cleient'
    }
  }
} 